import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"

import type { SteerNote } from "../types/steer-types"

import { CircleSlash, TriangleAlert } from "@/components/icons"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { useAIExplorerStore } from "@/stores/ai-explorer-store"

const EMPTY_NOTES: SteerNote[] = []

type FailureSteerStatus = "rejected" | "undelivered"
type FailureSteerNote = SteerNote & { status: FailureSteerStatus }

function isFailureSteerNote(note: SteerNote): note is FailureSteerNote {
  return note.status === "rejected" || note.status === "undelivered"
}

/**
 * Failure-only steering chips above the composer (issue #2438 / #2469).
 * Pending notes live in `ComposerQueueBar` until delivered; delivered
 * confirmation is the feed bubble + Activity History. This surface only
 * shows terminal failures: `rejected`, and queue-off `undelivered`.
 */
export function SteerNoteChips({ sessionType }: { sessionType?: string }) {
  const { t } = useTranslation()
  // Namespaced store factory — for modules that never populate steerNotes this
  // resolves to an (empty) store of their sessionType and stays inert.
  const store = useAIExplorerStore(sessionType ?? "ai-explorer")
  const steerNotes = store((state) => {
    const sessionId = state.currentSessionId
    if (!sessionId || !(sessionId in state.sessionStates)) return EMPTY_NOTES
    return state.sessionStates[sessionId].steerNotes
  })
  // Pending → queue bar; delivered → feed/activity. Filter outside the Zustand
  // selector so we don't return a fresh array reference on every store read
  // (that re-triggers React 19's snapshot loop).
  const visibleNotes = steerNotes.filter(isFailureSteerNote)

  if (visibleNotes.length === 0) {
    return null
  }

  const statusMeta: Record<
    FailureSteerStatus,
    { icon: ReactNode; className: string; tooltip: string }
  > = {
    undelivered: {
      icon: <CircleSlash className="size-3 shrink-0" />,
      className: "text-muted-foreground",
      tooltip: t("questioninput.steer_status_undelivered", {
        defaultValue: "The response finished before this note could be applied.",
      }),
    },
    rejected: {
      icon: <TriangleAlert className="size-3 shrink-0" />,
      className: "text-muted-foreground",
      tooltip: t("questioninput.steer_status_rejected", {
        defaultValue: "This note couldn't be accepted (too many notes or invalid content).",
      }),
    },
  }

  return (
    <div
      className="flex w-full max-w-3xl flex-wrap justify-end gap-1.5 px-1"
      data-testid="steer-note-chips"
    >
      {visibleNotes.map((note) => {
        const meta = statusMeta[note.status]
        return (
          <Tooltip key={note.steer_id}>
            <TooltipTrigger asChild>
              <span
                data-status={note.status}
                className={cn(
                  "bg-muted/60 flex max-w-[280px] items-center gap-1.5 rounded-md border px-2 py-1 text-xs",
                  meta.className
                )}
              >
                {meta.icon}
                <span className="truncate">{note.content}</span>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[250px]" side="top" sideOffset={5}>
              <p>{meta.tooltip}</p>
            </TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
}
